import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { configApi, type FeatureFlags } from '../services/configApi';

interface UseFeatureFlagsReturn {
  flags: FeatureFlags;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const useFeatureFlags = (): UseFeatureFlagsReturn => {
  // Cache-first: use sync read for initial render
  const [flags, setFlags] = useState<FeatureFlags>(() => configApi.getFlagsSync());
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const fetchFlags = useCallback(async () => {
    try {
      const next = await configApi.getFeatureFlags();
      if (isMountedRef.current) {
        setFlags(next);
      }
    } catch {
      if (isMountedRef.current) {
        setFlags(configApi.getFlagsSync());
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchFlags();
  }, [fetchFlags]);

  useEffect(() => {
    isMountedRef.current = true;

    // Async fetch
    setLoading(true);
    fetchFlags();

    // Refresh on foreground (visibility for web, appStateChange for native)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current) {
        fetchFlags();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let removeAppStateListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive && isMountedRef.current) {
          fetchFlags();
        }
      }).then((listener) => {
        removeAppStateListener = () => listener.remove();
      });
    }

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeAppStateListener?.();
    };
  }, [fetchFlags]);

  return {
    flags,
    loading,
    refresh,
  };
};
